import {
  SORT_COMPANY_ASC,
  SORT_COMPANY_DESC,
  SORT_CONTRACT_ASC,
  SORT_CONTRACT_DESC,
  SORT_GROUP_ASC,
  SORT_GROUP_DESC,
  SORT_NAME_ASC,
  SORT_NAME_DESC,
  SORT_EMAIL_ASC,
  SORT_EMAIL_DESC,
  SORT_STN_ASC,
  SORT_STN_DESC,
  SORT_ORDER_NUMBER_ASC,
  SORT_ORDER_NUMBER_DESC,
  SORT_RFQ_ASC,
  SORT_RFQ_DESC,
  SORT_AWARD_DATE_ASC,
  SORT_AWARD_DATE_DESC,
  SORT_DELIVERY_DATE_ASC,
  SORT_DELIVERY_DATE_DESC,
  SORT_ORDER_TOTAL_ASC,
  SORT_ORDER_TOTAL_DESC,
  SORT_ORDER_STATUS_ASC,
  SORT_ORDER_STATUS_DESC,
  SORT_SITE_NAME_ASC,
  SORT_SITE_NAME_DESC,
  SORT_CONTRACT_DATE_ASC,
  SORT_CONTRACT_DATE_DESC,
  SORT_SHORT_AGENCY_NAME_ASC,
  SORT_SHORT_AGENCY_NAME_DESC,
  FLAG_YES,
  FLAG_NO,
} from "../constants";

/**
 * Determines if the sortType is ASC
 * @param {number} sortType
 * @returns {boolean} is the sortType ASC?
 */
export const isAsc = (sortType) => typeof sortType === "number" && sortType % 2 === 1;

/**
 * Gets the corresponding sort type.
 * Ex) SORT_CONTRACT_ASC -> SORT_CONTRACT_DESC
 * @param {number} sortType
 * @returns {number} corresponding sortType
 */
export const getCorrespondingSort = (sortType) => sortType + (isAsc(sortType) ? 1 : -1);

/**
 * Generates a sorting onClick function for TableHeaderColumn.jsx
 * @param {number} descendingSort
 * @param {function} setSortOption
 * @returns {function}
 */
export const generateSort = (descendingSort, setSortOption) => () => {
  const ascendingSort = getCorrespondingSort(descendingSort);
  setSortOption((prevState) => (prevState === ascendingSort ? descendingSort : ascendingSort));
};

/**
 * Sorts based on chAddressType field of inputs
 * @param {Object} address1
 * @param {string} address1.chAddressType
 * @param {Object} address2
 * @param {string} address2.chAddressType
 * @returns {0 | 1 | -1}
 */
export const addressSort = ({ chAddressType: at1 }, { chAddressType: at2 }) => {
  if (at1 < at2) {
    return -1;
  } else if (at2 > at1) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * Sorts based on sortingNumber field of inputs
 * @param {Object} contact1
 * @param {number} contact1.sortingNumber
 * @param {Object} contact2
 * @param {number} contact2.sortingNumber
 * @returns {0 | 1 | -1}
 */
export const contactSort = ({ sortingNumber: sn1 }, { sortingNumber: sn2 }) => {
  if (sn1 < sn2) {
    return -1;
  } else if (sn2 > sn1) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * Sorts based on group field of inputs
 * @param {Object} contract1
 * @param {string} contract1.group
 * @param {Object} contract2
 * @param {string} contract2.group
 * @returns {0 | 1 | -1}
 */
export const contractSort = ({ group: g1 }, { group: g2 }) => {
  if (g1 < g2) {
    return -1;
  } else if (g1 > g2) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * Sorts based on:
 *    If mod number is 0, put first.
 *    Else sort in reverse order by mod number (highest first).
 * @param {Object} document1
 * @param {number} document1.contractModNumber
 * @param {Object} document2
 * @param {number} document2.contractModNumber
 * @returns {0 | 1 | -1}
 */
export const documentSort = ({ contractModNumber: cmn1 }, { contractModNumber: cmn2 }) => {
  if (cmn1 === 0) {
    return -1;
  } else if (cmn2 === 0) {
    return 1;
  } else if (cmn1 > cmn2) {
    return -1;
  } else if (cmn2 > cmn1) {
    return 1;
  } else {
    return 0;
  }
};

/**
 * Sorts based on:
 *    If phone is default and another isn't, sort default phone 1st.
 *    Else if phone type is 'Phone' and other isn't, sort 'Phone' 1st.
 * @param {Object} phoneNumber1
 * @param {FLAG_YES | FLAG_NO} phoneNumber1.defaultFlag
 * @param {string} phoneNumber1.typeFlag
 * @param {Object} phoneNumber2
 * @param {FLAG_YES | FLAG_NO} phoneNumber2.defaultFlag
 * @param {string} phoneNumber2.typeFlag
 * @returns {0 | 1 | -1}
 */
export const phoneNumberSort = ({ defaultFlag: df1, typeFlag: tf1 }, { defaultFlag: df2, typeFlag: tf2 }) => {
  if (df1 === FLAG_YES && df2 !== FLAG_YES) {
    return -1;
  } else if (df2 === FLAG_YES && df1 !== FLAG_YES) {
    return 1;
  } else {
    if (tf1 === "Phone") {
      return -1;
    } else if (tf2 === "Phone") {
      return 1;
    } else {
      return 0;
    }
  }
};

/**
 * Sorts alphabetically on object's provided key
 * @param {Object} a first item to sort
 * @param {Object} b second item to sort
 * @param {string} key object key on which to sort
 * @param {"asc" | "desc"} direction direction of sort
 * @returns {1 | -1 | 0}
 */
export const alphabeticalSort = (a, b, key, direction) => {
  if (!a || !b || !key || !a[key] || !b[key] || (direction !== "asc" && direction !== "desc")) {
    throw new Error("Invalid Parameters");
  }
  if (direction === "asc") {
    if(a[key].trim().toLowerCase() < b[key].trim().toLowerCase()) {
      return -1;
    } else if(a[key].trim().toLowerCase() > b[key].trim().toLowerCase()) {
      return 1;
    } else {
      return 0;
    }
  } else {
    if(a[key].trim().toLowerCase() < b[key].trim().toLowerCase()) {
      return 1;
    } else if(a[key].trim().toLowerCase() > b[key].trim().toLowerCase()) {
      return -1;
    } else {
      return 0;
    }
  }
};

/**
 * Sorts numerically on object's provided key
 * @param {Object} a first item to sort
 * @param {Object} b second item to sort
 * @param {string} key object key on which to sort
 * @param {"asc" | "desc"} direction direction of sort
 * @returns {1 | -1}
 */
export const numericalSort = (a, b, key, direction) => {
  if (!a || !b || !key || !a[key] || !b[key] || (direction !== "asc" && direction !== "desc")) {
    throw new Error("Invalid Parameters");
  }
  if (direction === "asc") {
    return a[key] < b[key] ? -1 : 1;
  } else {
    return a[key] < b[key] ? 1 : -1;
  }
};

/**
 * Sorts numerically on object's provided key
 * - undefined values are allowed and always sorted last
 * @param {Object} a first item to sort
 * @param {Object} b second item to sort
 * @param {string} key object key on which to sort
 * @param {"asc" | "desc"} direction direction of sort
 * @returns {1 | -1}
 */
export const numericalSortUndefined = (a, b, key, direction) => {
  if (
    !a ||
    !b ||
    !key ||
    typeof a !== "object" ||
    typeof a !== "object" ||
    (direction !== "asc" && direction !== "desc")
  ) {
    throw new Error("Invalid Parameters");
  }
  if (a[key] === null || a[key] === undefined) {
    return 1;
  } else if (b[key] === null || b[key] === undefined) {
    return -1;
  }
  if (direction === "asc") {
    return a[key] < b[key] ? -1 : 1;
  } else {
    return a[key] < b[key] ? 1 : -1;
  }
};

export const contractHolderSortMap = {
  [SORT_COMPANY_ASC]: (a, b) => alphabeticalSort(a, b, "fullName", "asc"),
  [SORT_COMPANY_DESC]: (a, b) => alphabeticalSort(a, b, "fullName", "desc"),
  [SORT_CONTRACT_ASC]: (a, b) => alphabeticalSort(a, b, "sewpContractNumber", "asc"),
  [SORT_CONTRACT_DESC]: (a, b) => alphabeticalSort(a, b, "sewpContractNumber", "desc"),
  [SORT_GROUP_ASC]: (a, b) => alphabeticalSort(a, b, "setAside", "asc"),
  [SORT_GROUP_DESC]: (a, b) => alphabeticalSort(a, b, "setAside", "desc"),
  [SORT_NAME_ASC]: (a, b) => alphabeticalSort(a, b, "lastName", "asc"),
  [SORT_NAME_DESC]: (a, b) => alphabeticalSort(a, b, "lastName", "desc"),
  [SORT_EMAIL_ASC]: (ch1, ch2) => alphabeticalSort(ch1, ch2, "email", "asc"),
  [SORT_EMAIL_DESC]: (ch1, ch2) => alphabeticalSort(ch1, ch2, "email", "desc"),
};

export const ordersListSortMap = {
  [SORT_STN_ASC]: (a, b) => numericalSort(a, b, "stnSeq", "asc"),
  [SORT_STN_DESC]: (a, b) => numericalSort(a, b, "stnSeq", "desc"),
  [SORT_ORDER_NUMBER_ASC]: (a, b) => alphabeticalSort(a, b, "orderNumber", "asc"),
  [SORT_ORDER_NUMBER_DESC]: (a, b) => alphabeticalSort(a, b, "orderNumber", "desc"),
  [SORT_RFQ_ASC]: (a, b) => numericalSortUndefined(a, b, "rfqSeq", "asc"),
  [SORT_RFQ_DESC]: (a, b) => numericalSortUndefined(a, b, "rfqSeq", "desc"),
  [SORT_COMPANY_ASC]: (a, b) => alphabeticalSort(a, b, "companyName", "asc"),
  [SORT_COMPANY_DESC]: (a, b) => alphabeticalSort(a, b, "companyName", "desc"),
  [SORT_AWARD_DATE_ASC]: (a, b) => alphabeticalSort(a, b, "awardDate", "asc"),
  [SORT_AWARD_DATE_DESC]: (a, b) => alphabeticalSort(a, b, "awardDate", "desc"),
  [SORT_DELIVERY_DATE_ASC]: (a, b) => alphabeticalSort(a, b, "expDeliveryDate", "asc"),
  [SORT_DELIVERY_DATE_DESC]: (a, b) => alphabeticalSort(a, b, "expDeliveryDate", "desc"),
  [SORT_ORDER_TOTAL_ASC]: (a, b) => numericalSort(a, b, "totalAmount", "asc"),
  [SORT_ORDER_TOTAL_DESC]: (a, b) => numericalSort(a, b, "totalAmount", "desc"),
  [SORT_ORDER_STATUS_ASC]: (a, b) => alphabeticalSort(a, b, "orderStatusDisplay", "asc"),
  [SORT_ORDER_STATUS_DESC]: (a, b) => alphabeticalSort(a, b, "orderStatusDisplay", "desc"),
};

export const approvedContractorListSortMap = {
  [SORT_SITE_NAME_ASC]: (a, b) => alphabeticalSort(a, b, "name", "asc"),
  [SORT_SITE_NAME_DESC]: (a, b) => alphabeticalSort(a, b, "name", "desc"),
  [SORT_CONTRACT_ASC]: (a, b) => alphabeticalSort(a, b, "federalContractNumber", "asc"),
  [SORT_CONTRACT_DESC]: (a, b) => alphabeticalSort(a, b, "federalContractNumber", "desc"),
  [SORT_CONTRACT_DATE_ASC]: (a, b) => numericalSort(a, b, "contractPopDate", "asc"),
  [SORT_CONTRACT_DATE_DESC]: (a, b) => numericalSort(a, b, "contractPopDate", "desc"),
  [SORT_SHORT_AGENCY_NAME_ASC]: (a, b) => alphabeticalSort(a, b, "shortAgencyName", "asc"),
  [SORT_SHORT_AGENCY_NAME_DESC]: (a, b) => alphabeticalSort(a, b, "shortAgencyName", "desc"),
};
